import { IPromise } from 'angular';

import { ITagWithCount } from '../directives/tbTagsInput';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode, TagErrorCode } from '../helpers/ErrorCodes';
import { ResultHelper } from '../helpers/ResultHelper';
import { TagsHelper } from '../helpers/TagsInputHelper';
import { DatasourceDefinition, FlowDefinition, ViewDefinition, WorkbookDefinition } from '../models/ContentDefinitions';
import { FlowListItemInfo } from '../models/FlowInfo';
import { IContentInfo } from '../models/IContentInfo';
import { IDataSourceBase } from '../models/IDataSourceInfo';
import { IViewInfo } from '../models/IViewInfo';
import { IWorkbookBase } from '../models/IWorkbookInfo';
import { IErrorItem } from '../models/MultipleItemResult';
import { INamedItemInfo, ITaggedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataSources } from '../resources/Datasources';
import { Flows } from '../resources/Flows';
import { Views } from '../resources/Views';
import { Workbooks } from '../resources/Workbooks';
import { IResult } from '../ServerApi/types/IResponse';
import { ActionHelpers } from './ActionHelpers';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { IErrorDetail, INotificationTranslationIds } from './ContentActionNotification';
import { IConfirmDialogTagsScope } from './ContentActions';
import { DataSourceErrors } from './DataSourceErrors';
import { FlowErrors } from './FlowErrors';
import { ViewErrors } from './ViewErrors';
import { WorkbookErrors } from './WorkbookErrors';

export interface INamedAndTaggedItemInfo extends INamedItemInfo, ITaggedItemInfo { }

export interface IChangeTagsExecuteFnParams<T> {
  changeTagsAction: (ids: string[], addTags: string[], removeTags: string[]) => IPromise<IResult>;
  getDescription: (itemSet: Set<T>) => string;
  notificationIds: INotificationTranslationIds;
  errorDetailFn: (errorItem: IErrorItem<T>) => IErrorDetail;
}

export interface IChangeTagsExecuteFnForMixedContentParams<T> {
  getDescription: (itemSet: T[]) => string;
  notificationIds: INotificationTranslationIds;
}

export class ChangeTagsActionHelperService {

  public static $inject = [
    '$q',
    'ConfirmActionDialog',
    'ActionHelpers',
    '$translate',
    'DataSources',
    'DataSourceErrors',
    'Views',
    'ViewErrors',
    'Workbooks',
    'WorkbookErrors',
    'Flows',
    'FlowErrors'
  ];

  constructor(
    private $q: ng.IQService,
    private ConfirmActionDialog: ConfirmActionDialog,
    private ActionHelpers: ActionHelpers,
    private $translate: ng.translate.ITranslateService,
    private DataSources: DataSources,
    private DataSourceErrors: DataSourceErrors,
    private Views: Views,
    private ViewErrors: ViewErrors,
    private Workbooks: Workbooks,
    private WorkbookErrors: WorkbookErrors,
    private Flows: Flows,
    private FlowErrors: FlowErrors
  ) { }

  public createExecuteFn<T extends INamedAndTaggedItemInfo>(params: IChangeTagsExecuteFnParams<T>) {
    return (itemSet: Set<T>, context: IActionContext) => {

      const scope = this.configureScope(context.scope, itemSet, params.getDescription);
      const previousTagSet = TagsHelper.tagSetFromTagsList(scope.tags.model);

      const dialogConfig = {
        scope: scope,
        bodyTemplateUrl: 'confirmActionDialogTags.html',
        title: this.$translate.instant('changeTags'),
        actionName: this.$translate.instant('changeTagsConfirm'),
        action: () => {
          const editTags = TagsHelper.getTagsChange(previousTagSet, scope.tags.model);
          return params.changeTagsAction(itemSet.keys(), editTags.addTags, editTags.removeTags).then(result => {
            return {
              ...result,
              tags: scope.tags.model.map(m => TagsHelper.unquote(m.name))
            };
          });
        },
        position: { reserveHeight: 450 }
      };

      const dialogInstance = this.ConfirmActionDialog.open(dialogConfig);

      dialogInstance.result
        .then(this.ActionHelpers.notifyStandardError(itemSet, params.notificationIds, params.errorDetailFn))
        .finally(() => scope.$destroy());

      return dialogInstance.result;
    };
  }

  public createExecuteFnForMixedContent(params: IChangeTagsExecuteFnForMixedContentParams<IContentInfo>) {
    return (items: IContentInfo[], context: IActionContext) => {
      const scope = this.configureScopeForMixedContent(context.scope, items, params.getDescription);
      const previousTagSet = TagsHelper.tagSetFromTagsList(scope.tags.model);

      const dialogConfig = {
        scope: scope,
        bodyTemplateUrl: 'confirmActionDialogTags.html',
        title: this.$translate.instant('changeTags'),
        actionName: this.$translate.instant('changeTagsConfirm'),
        action: () => this.changeTagsForContent(items, previousTagSet, scope),
        position: { reserveHeight: 450 }
      };

      const dialogInstance = this.ConfirmActionDialog.open(dialogConfig);

      dialogInstance.result
        .then((results: IResult[]) => ({
          ...ResultHelper.combineResults(results),
          tags: scope.tags.model.map(m => TagsHelper.unquote(m.name))
        }))
        .finally(() => scope.$destroy());

      return dialogInstance.result;
    };
  }

  private configureScope<T extends INamedAndTaggedItemInfo>(parentScope: ng.IScope, itemSet: Set<T>, getDescription: (itemSet: Set<T>) => string): IConfirmDialogTagsScope {
    const scope = <IConfirmDialogTagsScope>parentScope.$new();

    scope.description = getDescription(itemSet);

    scope.tags = {
      model: TagsHelper.tagsListFromTaggedItemSet(itemSet)
    };

    return scope;
  }

  private configureScopeForMixedContent<T>(parentScope: ng.IScope, items: T[], getDescription: (itemSet: T[]) => string): IConfirmDialogTagsScope {
    const scope = <IConfirmDialogTagsScope>parentScope.$new();

    scope.description = getDescription(items);

    scope.tags = {
      model: TagsHelper.tagsListFromTaggedItemArray(items)
    };

    return scope;
  }

  private changeTagsForContent(items: IContentInfo[], previousTagSet: Set<ITagWithCount>, scope: IConfirmDialogTagsScope): IPromise<IResult[]> {
    // We can only change the items to a set when we split by content type because different items of different content
    // types could have the same id. A set keyed on id would remove the 'duplicate' id, even though it corresponds to
    // a different item. Ids _are_ unique to content types so making them sets here is safe.
    const views = Set.fromIdItemArray(items.filter(i => i.contentType === ViewDefinition.contentType));
    const workbooks = Set.fromIdItemArray(items.filter(i => i.contentType === WorkbookDefinition.contentType));
    const datasources = Set.fromIdItemArray(items.filter(i => i.contentType === DatasourceDefinition.contentType));
    const flows = Set.fromIdItemArray(items.filter(i => i.contentType === FlowDefinition.contentType));

    const editTags = TagsHelper.getTagsChange(previousTagSet, scope.tags.model);
    const allResults: IResult[] = [];

    // We can't call these asynchronously because when adding a new tag, the backend creates an id for it. If
    // the calls are done at the same time, the server throws an error (NonUniqueResultException) about the
    // same tag getting two ids. Instead, we have to chain these calls and save the results from each one in an array.
    return this.changeTagsForWorkbooks(workbooks, editTags.addTags, editTags.removeTags)
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForFlows(flows, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForViews(views, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForDatasources(datasources, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return allResults;
      });
  }

  private changeTagsForViews(views: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (views.size() < 1) {
      return this.$q.when(null);
    }

    return this.Views.changeTags(views.keys(), addTags, removeTags).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        views,
        { successId: 'changeTagsViewsSuccess', errorId: 'changeTagsViewsError' },
        (errorItem: IErrorItem<IViewInfo>) => this.ViewErrors.errorDetailsFor(errorItem)
      )(result);
    });
  }

  private changeTagsForFlows(flows: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (flows.size() < 1) {
      return this.$q.when(null);
    }

    return this.Flows.changeTags(flows.keys(), addTags, removeTags).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        flows,
        { successId: 'changeTagsFlowsSuccess', errorId: 'changeTagsFlowsError' },
        (errorItem: IErrorItem<FlowListItemInfo>) => this.FlowErrors.errorDetailsFor(errorItem)
      )(result);
    });
  }

  private changeTagsForWorkbooks(workbooks: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (workbooks.size() < 1) {
      return this.$q.when(null);
    }

    return this.Workbooks.changeTags(workbooks.keys(), addTags, removeTags).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        workbooks,
        { successId: 'changeTagsWorkbooksSuccess', errorId: 'changeTagsWorkbooksError' },
        (errorItem: IErrorItem<IWorkbookBase>) => this.WorkbookErrors.errorDetailsFor(errorItem)
      )(result);
    });
  }

  private changeTagsForDatasources(datasources: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (datasources.size() < 1) {
      return this.$q.when(null);
    }

    return this.DataSources.changeTags(datasources.keys(), addTags, removeTags).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        datasources,
        { successId: 'changeTagsDatasourcesSuccess', errorId: 'changeTagsDatasourcesError' },
        (errorItem: IErrorItem<IDataSourceBase>) => this.DataSourceErrors.errorDetailsFor(errorItem)
      )(result);
    });
  }

  public errorMessageFor(errorItem: IErrorItem<any>): string {
    switch (errorItem.error.code) {
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return this.$translate.instant('changeTagsErrorNoPermission');
      case TagErrorCode.TagNameTooLong:
        return this.$translate.instant('changeTagsErrorNameTooLong');
      default:
        return this.$translate.instant('errorUnknown');
    }
  }

}

Module.instance().service('ChangeTagsActionHelperService', ChangeTagsActionHelperService);
